import { useEffect, useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/ContactForm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollProgress, ScrollToTop } from '@/components/ScrollAnimations';
import AnimatedPageHero from '@/components/AnimatedPageHero';
import { Helmet } from 'react-helmet-async';
import { certifications, partners, siteConfig } from '@/data/siteData';
import { 
  Award,
  Shield,
  CheckCircle2,
  Star,
  ArrowRight,
  ExternalLink,
  Sparkles
} from 'lucide-react';

const categories = ['All', 'Quality', 'Security', 'Partnership'];

export const PortfolioPage = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedCert, setSelectedCert] = useState(null);

  const filteredCerts = selectedCategory === 'All' 
    ? certifications 
    : certifications.filter(cert => cert.category === selectedCategory);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Portfolio - Felix Infotech | Certifications & Technology Partnerships</title>
        <meta name="description" content="Explore Felix Infotech's portfolio: ISO 9001, ISO 27001 certifications and strategic partnerships with HP, Dell, Cisco, Microsoft, VMware, and 40+ global technology leaders." />
        <meta name="keywords" content="Felix Infotech portfolio, IT certifications portfolio, technology partnerships, vendor certifications, ISO certified, enterprise IT portfolio India" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://felixinfotech.com/portfolio" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Portfolio - Felix Infotech | Certifications & Partnerships" />
        <meta property="og:description" content="ISO certified with 40+ global technology partnerships." />
        <meta property="og:url" content="https://felixinfotech.com/portfolio" />
        <meta property="og:site_name" content="Felix Infotech" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Felix Infotech Portfolio" />
        <meta name="twitter:description" content="Certifications and technology partnerships portfolio." />
      </Helmet>

      <ScrollProgress />
      <Navbar />

      <main>
        {/* Animated Hero Section */}
        <AnimatedPageHero
          title="Excellence Through Recognition"
          subtitle="Quality & Compliance"
          description="Industry certifications and partnerships that demonstrate our unwavering commitment to quality, security, and excellence."
          badge="Certifications & Partnerships"
          animationType="portfolio"
          gradient="from-amber-500 to-orange-600"
          breadcrumbs={[{ label: 'Portfolio' }]}
        />

        {/* Stats Section */}
        <section className="py-16 bg-muted">
          <div className="container mx-auto px-4 lg:px-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { value: '2', label: 'ISO Certifications' },
                { value: '15+', label: 'Technology Partners' },
                { value: siteConfig.experience, label: 'Years Experience' },
                { value: siteConfig.clients, label: 'Happy Clients' },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <p className="text-4xl font-display font-bold text-primary">{stat.value}</p>
                  <p className="text-muted-foreground">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Certifications Section */}
        <section className="py-20" ref={ref}>
          <div className="container mx-auto px-4 lg:px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              className="text-center mb-12"
            >
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground section-title">
                Our Certifications & Partnerships
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto mt-8">
                Recognized by industry-leading organizations for our commitment to excellence
              </p>
            </motion.div>

            {/* Filter */}
            <div className="flex justify-center gap-4 mb-12 flex-wrap">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-2 rounded-full transition-all ${
                    selectedCategory === category
                      ? 'bg-primary text-primary-foreground shadow-glow'
                      : 'bg-card border border-border hover:border-primary'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Certifications Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <AnimatePresence mode="popLayout">
                {filteredCerts.map((cert, index) => (
                  <motion.div
                    key={cert.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ y: -10 }}
                    onClick={() => setSelectedCert(cert)}
                    className="cursor-pointer"
                  >
                    <div className="h-full bg-card rounded-2xl p-6 shadow-soft border border-border hover:shadow-elevated hover:border-primary/50 transition-all group">
                      {/* Badge */}
                      <div className="flex justify-between items-start mb-4">
                        <Badge variant="outline" className={
                          cert.category === 'Quality' ? 'border-[#0066CC] text-[#0066CC]' :
                          cert.category === 'Security' ? 'border-[#0066CC] text-[#0066CC]' :
                          'border-[#0066CC] text-[#0066CC]'
                        }>
                          {cert.category}
                        </Badge>
                        <Sparkles className="w-5 h-5 text-secondary opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>

                      {/* Logo */}
                      <div className="h-24 flex items-center justify-center mb-4">
                        <img
                          src={cert.image}
                          alt={cert.title}
                          className="max-h-full max-w-full object-contain"
                        />
                      </div>

                      {/* Info */}
                      <h3 className="font-display font-semibold text-lg mb-2 group-hover:text-primary transition-colors">
                        {cert.title}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {cert.description}
                      </p>

                      {/* View More */}
                      <div className="mt-4 flex items-center gap-1 text-sm text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                        View Details <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        </section>

        {/* ISO Certifications Highlight */}
        <section className="py-20 bg-muted">
          <div className="container mx-auto px-4 lg:px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground section-title">
                ISO Certified Excellence
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* ISO 9001 */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-card rounded-3xl p-8 shadow-elevated border border-border"
              >
                <div className="flex items-center gap-6 mb-6">
                  <div className="w-24 h-24 flex items-center justify-center">
                    <img src="https://felixinfotech.com/img/iso-1.png" alt="ISO 9001" className="max-w-full max-h-full" />
                  </div>
                  <div>
                    <h3 className="font-display text-2xl font-bold">ISO 9001:2015</h3>
                    <p className="text-primary font-medium">Quality Management</p>
                  </div>
                </div>
                <p className="text-muted-foreground mb-6">
                  Our ISO 9001 certification demonstrates our commitment to consistent quality in all processes, 
                  ensuring customer satisfaction through effective quality management systems.
                </p>
                <div className="space-y-2">
                  {['Process Excellence', 'Customer Focus', 'Continuous Improvement', 'Risk Management'].map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-[#0066CC]" />
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* ISO 27001 */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-card rounded-3xl p-8 shadow-elevated border border-border"
              >
                <div className="flex items-center gap-6 mb-6">
                  <div className="w-24 h-24 flex items-center justify-center">
                    <img src="https://felixinfotech.com/img/iso-2.png" alt="ISO 27001" className="max-w-full max-h-full" />
                  </div>
                  <div>
                    <h3 className="font-display text-2xl font-bold">ISO 27001:2013</h3>
                    <p className="text-primary font-medium">Information Security</p>
                  </div>
                </div>
                <p className="text-muted-foreground mb-6">
                  Our ISO 27001 certification ensures the highest standards of information security, 
                  protecting your data through comprehensive security management practices.
                </p>
                <div className="space-y-2">
                  {['Data Protection', 'Access Control', 'Incident Management', 'Business Continuity'].map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5 text-blue-500" />
                      <span className="text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Technology Partners */}
        <section className="py-20">
          <div className="container mx-auto px-4 lg:px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-foreground section-title">
                Technology Partners
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto mt-8">
                We partner with industry leaders to deliver best-in-class solutions
              </p>
            </motion.div>

            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-6">
              {partners.map((partner, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="rounded-xl p-6 flex items-center justify-center hover:shadow-md transition-all h-24"
                >
                  <img
                    src={partner.logo}
                    alt={partner.name}
                    title={partner.name}
                    className="max-w-full max-h-full object-contain filter grayscale hover:grayscale-0 transition-all"
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Modal */}
        <AnimatePresence>
          {selectedCert && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-foreground/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedCert(null)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-card rounded-3xl p-8 max-w-lg w-full shadow-float border border-border"
              >
                <div className="flex justify-between items-start mb-6">
                  <Badge variant="outline" className={
                    selectedCert.category === 'Quality' ? 'border-[#0066CC] text-[#0066CC]' :
                    selectedCert.category === 'Security' ? 'border-[#0066CC] text-[#0066CC]' :
                    'border-[#0066CC] text-[#0066CC]'
                  }>
                    {selectedCert.category}
                  </Badge>
                  <button
                    onClick={() => setSelectedCert(null)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    ✕
                  </button>
                </div>

                <div className="h-32 flex items-center justify-center mb-6">
                  <img
                    src={selectedCert.image}
                    alt={selectedCert.title}
                    className="max-h-full max-w-full object-contain"
                    style={{ mixBlendMode: 'multiply' }}
                  />
                </div>

                <h3 className="font-display text-2xl font-bold mb-3">{selectedCert.title}</h3>
                <p className="text-muted-foreground mb-6">{selectedCert.description}</p>

                <Button className="w-full" onClick={() => setSelectedCert(null)}>
                  Close
                </Button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <ContactForm variant="simple" />
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
};

export default PortfolioPage;
